import React, { useState, useEffect } from 'react';
import { Plus, Calendar, ChevronDown, ChevronRight, LayoutGrid, List, Pencil, Trash2, X, Shield, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const ClubRequests = () => {
  const [requests, setRequests] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [expandedClubs, setExpandedClubs] = useState({});
  const [viewMode, setViewMode] = useState('panels');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    club_id: '', window: 'summer', player_type: '', transfer_type: 'transfer', position: '', price_range: '', last_contact_date: '', notes: ''
  });

  useEffect(() => { fetchRequests(); fetchClubs(); }, []);
  useEffect(() => {
    const expanded = {};
    clubs.forEach(c => { expanded[c.id] = true; });
    setExpandedClubs(expanded);
  }, [clubs]);

  const fetchRequests = async () => {
    try { setRequests((await api.get('/club-requests')).data); } catch { toast.error('Failed to load requests'); }
  };
  const fetchClubs = async () => {
    try { setClubs((await api.get('/clubs')).data); } catch { toast.error('Failed to load clubs'); }
  };

  const resetForm = () => {
    setFormData({ club_id: '', window: 'summer', player_type: '', transfer_type: 'transfer', position: '', price_range: '', last_contact_date: '', notes: '' });
    setEditingRequest(null);
    setShowForm(false);
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    setFormData({
      club_id: request.club_id || '', window: request.window || 'summer', player_type: request.player_type || '',
      transfer_type: request.transfer_type || 'transfer', position: request.position || '',
      price_range: request.price_range || '', last_contact_date: request.last_contact_date || '', notes: request.notes || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRequest) {
        await api.put(`/club-requests/${editingRequest.id}`, formData);
        toast.success('Request updated');
      } else {
        await api.post('/club-requests', formData);
        toast.success('Club request created');
      }
      resetForm();
      fetchRequests();
    } catch { toast.error(editingRequest ? 'Failed to update' : 'Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    try { await api.delete(`/club-requests/${id}`); toast.success('Deleted'); fetchRequests(); } catch { toast.error('Failed to delete'); }
  };

  const toggleClub = (clubId) => setExpandedClubs(prev => ({ ...prev, [clubId]: !prev[clubId] }));

  const getWindowColor = (w) => w === 'summer' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  const getTransferTypeColor = (t) => ({ transfer: 'bg-green-500/20 text-green-400', loan: 'bg-white/20 text-white', free_transfer: 'bg-purple-500/20 text-purple-400' }[t] || 'bg-green-500/20 text-green-400');

  const positions = ['GK', 'LB', 'LCB', 'CB', 'RCB', 'RB', 'DM', 'LCM', 'CM', 'RCM', 'AM', 'LW', 'RW', 'CF'];

  // Filter requests by search term (position first, then player type, club name)
  const filteredRequests = requests.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (r.position && r.position.toLowerCase().includes(term)) ||
           (r.player_type && r.player_type.toLowerCase().includes(term)) ||
           (r.club_name && r.club_name.toLowerCase().includes(term)) ||
           (r.notes && r.notes.toLowerCase().includes(term));
  });

  // Group filtered requests by club
  const requestsByClub = {};
  filteredRequests.forEach(request => {
    if (!requestsByClub[request.club_id]) {
      const club = clubs.find(c => c.id === request.club_id);
      requestsByClub[request.club_id] = { club: club || { name: request.club_name, id: request.club_id }, requests: [] };
    }
    requestsByClub[request.club_id].requests.push(request);
  });
  const clubsWithRequests = Object.values(requestsByClub).sort((a, b) => a.club.name.localeCompare(b.club.name));

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">Club Requests</h1>
              <p className="text-lg text-zinc-300">{clubsWithRequests.length} clubs &bull; {filteredRequests.length} requests</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-zinc-900/50 border border-white/10 rounded-sm overflow-hidden">
                <button onClick={() => setViewMode('panels')} data-testid="view-mode-panels"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${viewMode === 'panels' ? 'bg-white text-black' : 'text-white hover:bg-white/10'}`}>
                  <LayoutGrid className="w-4 h-4" /><span className="hidden sm:inline">Panels</span>
                </button>
                <button onClick={() => setViewMode('list')} data-testid="view-mode-list"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${viewMode === 'list' ? 'bg-white text-black' : 'text-white hover:bg-white/10'}`}>
                  <List className="w-4 h-4" /><span className="hidden sm:inline">List</span>
                </button>
              </div>
              <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-request-button"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3">
                <Plus className="w-5 h-5" /> New Request
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Search */}
        <div className="glass-card p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
            <input type="text" placeholder="Search by position (e.g. CM, LW), player type, or club name..."
              data-testid="search-club-requests" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
            />
          </div>
          {/* Quick position filters */}
          <div className="flex flex-wrap gap-2 mt-3">
            {positions.map(pos => (
              <button key={pos} onClick={() => setSearchTerm(searchTerm === pos ? '' : pos)}
                className={`px-3 py-1 text-xs font-bold uppercase rounded-sm transition-all ${searchTerm === pos ? 'bg-white text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'}`}>
                {pos}
              </button>
            ))}
          </div>
        </div>

        {showForm && (
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold uppercase text-white">{editingRequest ? 'Edit Request' : 'New Club Request'}</h2>
              <button onClick={resetForm} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Club *</label>
                  <select required value={formData.club_id} onChange={(e) => setFormData({ ...formData, club_id: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none">
                    <option value="">Select Club</option>
                    {clubs.map(club => <option key={club.id} value={club.id}>{club.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Position *</label>
                  <select required value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white outline-none">
                    <option value="">Select Position</option>
                    {positions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Player Type *</label>
                  <input type="text" required placeholder="e.g., Creative midfielder" value={formData.player_type}
                    onChange={(e) => setFormData({ ...formData, player_type: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Transfer Window</label>
                  <select value={formData.window} onChange={(e) => setFormData({ ...formData, window: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none">
                    <option value="summer">Summer Window</option>
                    <option value="winter">Winter Window</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Transfer Type</label>
                  <select value={formData.transfer_type} onChange={(e) => setFormData({ ...formData, transfer_type: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none">
                    <option value="transfer">Transfer</option>
                    <option value="loan">Loan</option>
                    <option value="free_transfer">Free Transfer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Price Range</label>
                  <input type="text" placeholder="e.g., £5k-£10k" value={formData.price_range}
                    onChange={(e) => setFormData({ ...formData, price_range: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Last Contact Date</label>
                  <input type="date" value={formData.last_contact_date || ''}
                    onChange={(e) => setFormData({ ...formData, last_contact_date: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Notes</label>
                  <textarea rows="3" placeholder="Additional notes" value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-4 py-3 text-white outline-none" />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="bg-white text-black hover:bg-zinc-200 font-bold uppercase px-6 py-2 rounded-sm">
                  {editingRequest ? 'Update Request' : 'Save Request'}
                </button>
                <button type="button" onClick={resetForm} className="bg-white/10 text-white hover:bg-white/20 px-6 py-2 rounded-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {filteredRequests.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-zinc-500 text-lg">{searchTerm ? 'No requests match your search.' : 'No club requests yet.'}</p>
          </div>
        ) : viewMode === 'panels' ? (
          <div className="space-y-6">
            {clubsWithRequests.map(({ club, requests: clubReqs }) => (
              <div key={club.id} className="glass-card overflow-hidden" data-testid={`club-panel-${club.id}`}>
                <div onClick={() => toggleClub(club.id)} className="p-6 cursor-pointer hover:bg-white/5 transition-colors flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {club.badge_url ? (
                      <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden border border-white/10">
                        <img src={club.badge_url} alt={club.name} className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className="p-3 bg-blue-500/10 rounded-lg"><Shield className="w-6 h-6 text-blue-400" strokeWidth={1.5} /></div>
                    )}
                    <div>
                      <h2 className="text-2xl font-bold text-white">{club.name}</h2>
                      <p className="text-sm text-zinc-400">{clubReqs.length} request{clubReqs.length !== 1 ? 's' : ''}{club.league ? ` • ${club.league}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="hidden md:flex gap-2">
                      {['summer', 'winter'].map(w => {
                        const count = clubReqs.filter(r => r.window === w).length;
                        return count > 0 ? <span key={w} className={`px-2 py-1 rounded text-xs font-bold border ${getWindowColor(w)}`}>{count} {w}</span> : null;
                      })}
                    </div>
                    {expandedClubs[club.id] ? <ChevronDown className="w-6 h-6 text-zinc-400" /> : <ChevronRight className="w-6 h-6 text-zinc-400" />}
                  </div>
                </div>
                {expandedClubs[club.id] && (
                  <div className="border-t border-white/5">
                    <div className="p-4 bg-zinc-900/30">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {clubReqs.map((request) => (
                          <div key={request.id} className="p-4 bg-black/30 border border-white/5 rounded-lg" data-testid={`request-item-${request.id}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="px-3 py-1 bg-white/20 text-white text-sm font-black rounded">{request.position}</span>
                                  <span className={`px-2 py-1 rounded text-xs font-bold border ${getWindowColor(request.window)}`}>
                                    {request.window}
                                  </span>
                                </div>
                                <p className="font-bold text-white">{request.player_type}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleEdit(request)} className="p-1.5 hover:bg-white/10 rounded transition-colors" title="Edit">
                                  <Pencil className="w-3.5 h-3.5 text-zinc-400 hover:text-white" />
                                </button>
                                <button onClick={() => handleDelete(request.id)} className="p-1.5 hover:bg-red-500/20 rounded transition-colors" title="Delete">
                                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${getTransferTypeColor(request.transfer_type)}`}>
                                {request.transfer_type.replace('_', ' ')}
                              </span>
                              {request.price_range && <span className="text-sm font-bold text-white">{request.price_range}</span>}
                            </div>
                            {request.last_contact_date && (
                              <div className="flex items-center gap-1 text-xs text-zinc-400 mb-2">
                                <Calendar className="w-3 h-3" /> Last contact: {formatDate(request.last_contact_date)}
                              </div>
                            )}
                            {request.notes && <p className="text-xs text-zinc-400 p-2 bg-white/5 rounded">{request.notes}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-black/30">
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Position</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Club</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Player Type</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Window</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Type</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Budget</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Last Contact</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-widest text-zinc-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...filteredRequests].sort((a, b) => (a.club_name || '').localeCompare(b.club_name || '')).map((request) => (
                  <tr key={request.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-4"><span className="px-3 py-1 bg-white/20 text-white text-xs font-black rounded">{request.position}</span></td>
                    <td className="p-4"><span className="font-bold text-white">{request.club_name}</span></td>
                    <td className="p-4 text-zinc-400">{request.player_type}</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold border ${getWindowColor(request.window)}`}>{request.window}</span></td>
                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold ${getTransferTypeColor(request.transfer_type)}`}>{request.transfer_type.replace('_', ' ')}</span></td>
                    <td className="p-4 text-white font-bold">{request.price_range || '-'}</td>
                    <td className="p-4 text-zinc-400 text-sm">{request.last_contact_date ? formatDate(request.last_contact_date) : '-'}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleEdit(request)} className="p-1.5 hover:bg-white/10 rounded"><Pencil className="w-4 h-4 text-zinc-400 hover:text-white" /></button>
                        <button onClick={() => handleDelete(request.id)} className="p-1.5 hover:bg-red-500/20 rounded"><Trash2 className="w-4 h-4 text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubRequests;

